import { clone, cloneDeep } from 'lodash-es';
import { roundForDatabase } from '@/components/map/utils/number-utils';
import { PARCELS_LAYER_ID } from '@/components/maplibre/consts';
import type { StyleConfig } from '@/components/maplibre/types';
import { APP_SETTINGS } from '@/lib/config';
import { getBorderDashArray, getBorderLineGapWidth } from './style-borders';
import { DEFAULT_STYLE_V2, DEFAULT_STYLE_VALUES, LABEL_FONT_STYLES } from './style-constants';
import { type StyleV2, createStaticMode, toLabelSizeMode } from './style-v2';

export const getDefaultStyles = (): StyleV2 => {
  const defaultStyles: StyleV2 = { version: APP_SETTINGS.CURRENT_STYLE_VERSION };
  defaultStyles.point = DEFAULT_STYLE_V2.point;
  defaultStyles.polygon = DEFAULT_STYLE_V2.polygon;
  defaultStyles.line = DEFAULT_STYLE_V2.line;

  return clone(defaultStyles);
};

/**
 * Creates default style configuration for any layer type
 * Single source of truth for all default styling
 */
export function createDefaultStyle(): StyleConfig {
  return {
    circle: {
      'circle-radius': roundForDatabase(DEFAULT_STYLE_VALUES.size),
      'circle-color': DEFAULT_STYLE_VALUES.fillColor,
      'circle-opacity': roundForDatabase(DEFAULT_STYLE_VALUES.fillOpacity / 100),
      'circle-stroke-color': DEFAULT_STYLE_VALUES.strokeColor,
      'circle-stroke-width': roundForDatabase(DEFAULT_STYLE_VALUES.strokeWidth),
      'circle-stroke-opacity': 1,
    },
    fill: {
      'fill-color': DEFAULT_STYLE_VALUES.polygonFillColor,
      'fill-opacity': roundForDatabase(DEFAULT_STYLE_VALUES.polygonFillOpacity / 100),
    },
    line: {
      'line-color': DEFAULT_STYLE_VALUES.polygonStrokeColor,
      'line-width': roundForDatabase(DEFAULT_STYLE_VALUES.polygonStrokeWidth),
      'line-opacity': roundForDatabase(DEFAULT_STYLE_VALUES.polygonStrokeOpacity / 100),
      'line-dasharray': getBorderDashArray(DEFAULT_STYLE_VALUES.borderType) || [],
      'line-gap-width': getBorderLineGapWidth(DEFAULT_STYLE_VALUES.borderType),
    },
    colorLogic: {
      colorLogicColumn: DEFAULT_STYLE_VALUES.colorLogicColumn,
      colorLogicScheme: DEFAULT_STYLE_VALUES.colorLogicScheme,
      colorLogicType: DEFAULT_STYLE_VALUES.colorLogicType,
      colorLogicEnabled: DEFAULT_STYLE_VALUES.colorLogicEnabled,
    },
    sizeLogic: {
      sizeBy: DEFAULT_STYLE_VALUES.sizeLogic.sizeBy,
      minSize: roundForDatabase(DEFAULT_STYLE_VALUES.sizeLogic.minSize),
      maxSize: roundForDatabase(DEFAULT_STYLE_VALUES.sizeLogic.maxSize),
      direction: DEFAULT_STYLE_VALUES.sizeLogic.direction,
    },
    labelLogic: {
      labelBy: DEFAULT_STYLE_VALUES.labelLogic.labelBy,
      size: toLabelSizeMode(DEFAULT_STYLE_VALUES.labelLogic.size),
      color: DEFAULT_STYLE_VALUES.labelLogic.color,
      haloColor: DEFAULT_STYLE_VALUES.labelLogic.haloColor,
      haloWidth: toLabelSizeMode(roundForDatabase(DEFAULT_STYLE_VALUES.labelLogic.haloWidth)),
      fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
      textVariableAnchor: DEFAULT_STYLE_VALUES.labelLogic.textVariableAnchor,
      textRadialOffset: DEFAULT_STYLE_VALUES.labelLogic.textRadialOffset,
    },
  };
}

/**
 * Creates default StyleConfig for parcel layers with red styling
 * This format matches what parseStoredStyleData expects
 */
export function createParcelDefaultStyleConfig() {
  return {
    circle: {
      'circle-radius': 6,
      'circle-color': '#FF0000',
      'circle-opacity': 1,
      'circle-stroke-color': '#FF0000',
      'circle-stroke-width': 1,
      'circle-stroke-opacity': 1,
    },
    fill: {
      'fill-color': '#FF0000',
      'fill-opacity': 0,
    },
    line: {
      'line-color': '#FF0000',
      'line-width': 1,
      'line-opacity': 1,
    },
    colorLogic: {
      colorLogicColumn: '',
      colorLogicScheme: '',
      colorLogicType: 'category',
      colorLogicEnabled: false,
    },
  };
}

export function createDefaultStyleV2(): StyleV2 {
  return {
    version: APP_SETTINGS.CURRENT_STYLE_VERSION,
    polygon: {
      fill: {
        color: createStaticMode(DEFAULT_STYLE_VALUES.polygonFillColor),
        opacity: createStaticMode(DEFAULT_STYLE_VALUES.polygonFillOpacity / 100),
      },
      border: {
        color: createStaticMode(DEFAULT_STYLE_VALUES.polygonStrokeColor),
        opacity: createStaticMode(DEFAULT_STYLE_VALUES.polygonStrokeOpacity / 100),
        width: createStaticMode(DEFAULT_STYLE_VALUES.polygonStrokeWidth),
        borderType: DEFAULT_STYLE_VALUES.borderType,
      },
      label: {
        columnId: null,
        size: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.size),
        color: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.color),
        haloColor: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.haloColor),
        haloWidth: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.haloWidth),
        fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
      },
    },
    line: {
      color: createStaticMode(DEFAULT_STYLE_VALUES.lineColor),
      opacity: createStaticMode(DEFAULT_STYLE_VALUES.lineOpacity / 100),
      width: createStaticMode(DEFAULT_STYLE_VALUES.lineWidth),
      borderType: DEFAULT_STYLE_VALUES.borderType,
    },
    point: {
      icon: createStaticMode(DEFAULT_STYLE_VALUES.iconId),
      size: createStaticMode(DEFAULT_STYLE_VALUES.size),
      color: createStaticMode(DEFAULT_STYLE_VALUES.fillColor),
      opacity: createStaticMode(DEFAULT_STYLE_VALUES.fillOpacity / 100),
      strokeColor: createStaticMode(DEFAULT_STYLE_VALUES.strokeColor),
      strokeOpacity: createStaticMode(DEFAULT_STYLE_VALUES.strokeOpacity / 100),
      strokeWidth: createStaticMode(DEFAULT_STYLE_VALUES.strokeWidth),
      label: {
        columnId: null,
        size: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.size),
        color: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.color),
        haloColor: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.haloColor),
        haloWidth: createStaticMode(DEFAULT_STYLE_VALUES.labelLogic.haloWidth),
        fontStyle: DEFAULT_STYLE_VALUES.labelLogic.fontStyle,
      },
    },
  };
}

/**
 * Creates default StyleV2 for parcel layers with red styling
 */
export function createParcelDefaultStyleV2(): StyleV2 {
  return {
    version: APP_SETTINGS.CURRENT_STYLE_VERSION,
    polygon: {
      fill: {
        color: createStaticMode('#FF0000'),
        opacity: createStaticMode(0),
      },
      border: {
        color: createStaticMode('#FF0000'),
        opacity: createStaticMode(1),
        width: createStaticMode(1),
        borderType: 'solid',
      },
      label: {
        columnId: null,
        size: createStaticMode(6),
        color: createStaticMode('#FF0000'),
        haloColor: createStaticMode('#FF0000'),
        haloWidth: createStaticMode(1),
        fontStyle: LABEL_FONT_STYLES.REGULAR,
      },
    },
  };
}

/**
 * Gets the appropriate default style for a specific layer
 * Centralized logic for layer-specific default styling
 * @deprecated
 * Use getDefaultStyleV2ForLayer instead
 */
export function getDefaultStyleForLayer(layerId: string) {
  if (layerId === PARCELS_LAYER_ID) {
    return createParcelDefaultStyleConfig();
  }

  return createDefaultStyle();
}

export function getDefaultStyleV2ForLayer(layerId: string, color?: string): StyleV2 {
  if (layerId === PARCELS_LAYER_ID) {
    return createParcelDefaultStyleV2();
  }

  return color ? getDefaultStylesWithColor(color) : getDefaultStyles();
}

/**
 * Returns the default StyleV2 with polygon fill / line / point colors
 * overridden to `color`. Leaves stroke, opacity, label, etc. untouched.
 *
 * NB: `getDefaultStyles()` returns a shallow clone of DEFAULT_STYLE_V2, so
 * nested sections (polygon/fill/etc.) are still references. Deep-clone
 * before mutating so each call produces an independent style object.
 */
function getDefaultStylesWithColor(color: string): StyleV2 {
  const base = cloneDeep(getDefaultStyles());
  if (base.polygon?.fill) {
    base.polygon.fill.color = createStaticMode(color);
  }
  if (base.line) {
    base.line.color = createStaticMode(color);
  }
  if (base.point) {
    base.point.color = createStaticMode(color);
  }
  return base;
}
